import React, { useState, useEffect, useMemo } from "react"
import withStyles from "react-jss"
import { Question } from "../components"
import { data } from "../data/data"
import { RESULT_IDS } from "../data/uniqueValues"
import { FADEOUT } from "../data/globalStyles"
import { formatUserChoicesData, formatShowNewQuestion } from "../utils/helper"
import {
  QUIZ_FADEIN,
  QUIZ_FADEOUT,
  SECTION_DURATION,
} from "../data/globalStyles"
import { gif1, gif2, gif3, gif4, gif5 } from "../assets/images"
import { preloadImages } from "../utils/preloadImages"

const styles = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    height: "100vh",
    animation: ({ show }) =>
      `${
        show ? `$${QUIZ_FADEIN}` : `$${QUIZ_FADEOUT}`
      } ${SECTION_DURATION} ease-in-out`,
  },
  results: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    background: "white",
    color: "black",
    borderBottom: "1px solid black",
    padding: "1rem",
  },
  [`@keyframes ${QUIZ_FADEIN}`]: {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
  [`@keyframes ${QUIZ_FADEOUT}`]: {
    from: {
      opacity: 1,
    },
    to: {
      opacity: 0,
    },
  },
}

const Quiz = ({
  classes,
  setShow,
  section,
  onAnimationEnd,
  setCustomizedMenu,
  resetQuiz,
  setResetQuiz,
}) => {
  const INITIAL_USER_CHOICE_DATA = useMemo(
    () => formatUserChoicesData([], data[0]),
    []
  )

  // Master list containing all user choices
  const [userChoices, setUserChoices] = useState(() => INITIAL_USER_CHOICE_DATA)

  const [renderData, setRenderData] = useState(userChoices)
  const [nextQ, setNextQ] = useState({})

  const [possibleResults, setPossibleResults] = useState(RESULT_IDS)
  const [showResults, setShowResults] = useState(false)

  const updateChoice = (qId, selectedOptionId) => {
    // Find the selected answer in userChoices
    const selectedOptionData = userChoices
      .find((d) => d.qId === qId)
      .options.find((d) => d.optionId === selectedOptionId)

    // Update the user's choice
    const updatedUserChoices = userChoices.map((d) => {
      if (d.qId === qId) {
        return {
          ...d,
          options: d.options.map((option) => ({
            ...option,
            selected: selectedOptionId === option.optionId ? true : false,
          })),
        }
      } else return d
    })

    // Narrow down user's results using possible results of selected answer
    const filteredPossibleResults = possibleResults.filter((id) =>
      selectedOptionData.possibleResultsIds.includes(id)
    )
    setPossibleResults(filteredPossibleResults)

    const nextQId = selectedOptionData.nextQId
    // If the quiz hasn't ended and there are next questions to ask
    if (nextQId) {
      const nextQuestion = data.find((d) => d.qId === nextQId)
      const formattedNextQuestion = formatShowNewQuestion(nextQuestion)
      setNextQ(formattedNextQuestion)
      const userChoiceWithNextQ = formatUserChoicesData(
        updatedUserChoices,
        formattedNextQuestion
      )
      setUserChoices(userChoiceWithNextQ)

      // Hide the old question, which will trigger onAnimationEnd()
      setRenderData((data) => [
        {
          ...data[0],
          showQuestion: false,
        },
      ])
    } else {
      // Show result only if one result is set
      if (filteredPossibleResults.length === 1) {
        setShowResults(true)
        // Hide the old question, which will trigger onAnimationEnd()
        setRenderData((data) => [
          {
            ...data[0],
            showQuestion: false,
          },
        ])
        setCustomizedMenu(filteredPossibleResults)
      } else {
        // Single result not set, no next question found
      }
    }
  }

  // When user clicks on an option, it triggers CSS animation.
  // This function gets triggered when it ends
  const onQuizAnimationEnd = ({ animationName }) => {
    // Hide quiz section and show results
    if (
      showResults &&
      renderData[0].showResult &&
      animationName.includes(FADEOUT)
    ) {
      // setRenderData([])
      // setShow((d) => ({
      //   ...d,
      //   quiz: false,
      //   results: true,
      // }))
    }
    // If menu is finalized, show loading for 3s, then switch to result
    else if (showResults && animationName.includes(FADEOUT)) {
      setRenderData([
        {
          loading: true,
          text: "whipping up your recipe ...",
          showQuestion: true,
          showResult: false,
          imgSrc: gif5,
          alt: "whipping up your recipe ...",
        },
      ])
      setTimeout(() => {
        setShow((d) => ({
          ...d,
          quiz: false,
          results: true,
        }))
      }, 2000)
    }
    // Show next question
    else if (
      animationName.includes(FADEOUT) &&
      renderData[0].loading === true &&
      renderData[0].showQuestion === false
    ) {
      const formattedQ = formatShowNewQuestion(nextQ)
      setRenderData([formattedQ])
    }
    // If transitioning to next question, show a GIF first
    else if (animationName.includes(FADEOUT)) {
      const gifArray = [
        gif1,
        gif2,
        gif3,
        gif4,
        gif1,
        gif2,
        gif3,
        gif4,
        gif1,
        gif2,
        gif3,
        gif4,
        gif1,
      ]
      setRenderData([
        {
          loading: true,
          showQuestion: true,
          showResult: false,
          imgSrc: gifArray[formatShowNewQuestion(nextQ).qId - 1],
          qId: formatShowNewQuestion(nextQ).qId,
        },
      ])
      setTimeout(() => {
        setRenderData((d) => [
          {
            ...d[0],
            showQuestion: false,
          },
        ])
      }, 1500)
    }
  }

  useEffect(() => {
    if (resetQuiz) {
      setUserChoices(INITIAL_USER_CHOICE_DATA)
      setRenderData(INITIAL_USER_CHOICE_DATA)
      setNextQ({})
      setPossibleResults(RESULT_IDS)
      setShowResults(false)
      setResetQuiz(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetQuiz])

  useEffect(() => {
    preloadImages([gif1, gif2, gif3, gif4, gif5])
  }, [])

  return (
    section === "quiz" && (
      <section className={classes.container} onAnimationEnd={onAnimationEnd}>
        <Question
          data={renderData}
          onAnimationEnd={onQuizAnimationEnd}
          onClick={updateChoice}
        />
        {/* For debugging */}
        {/* <div className={classes.results}>
          {possibleResults ? possibleResults.join(", ") : "No results"}
        </div> */}
      </section>
    )
  )
}

export default withStyles(styles)(Quiz)
